import AIFillStructuredTableModal from 'notebook/ai-tools/flexible-data-entry/AIFillStructuredTableModal';
import * as NoteLinkedFileDAO from 'notebook/notes/attachments/NoteLinkedFileDAO';
// @ts-expect-error
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import NoteConstants from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {Row} from 'notebook/tables/TableConstants';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import runModal from 'ui-library/runModal';
import * as DateUtil from 'util/DateUtil';
import {withLoadingScreen} from 'util/Promises';

export interface AddStructuredTableRowsUsingAIResult {
  /**
   * New rows to add to the table. We ensure that these are valid, e.g. do not edit read-only columns.
   */
  newRows: Array<Row>;
  /**
   * Note parts to append after the table. This is used to attach user-uploaded files if necessary.
   */
  noteParts: Array<NotePart>;
  cidIdMap: CidIdMap;
}

/**
 * For a given structured table, prompt the user to add new rows, with details inferred by AI.
 *
 * Currently, we only allow adding rows, though in the future we may want to support edits and
 * other operations.
 *
 * Note that this currently should only be used for structured tables where column metadata is
 * enough to know if a cell is fillable by the user. See ai_data_entry_notebook_util.py on the
 * backend for details.
 */
export default async function addStructuredTableRowsUsingAI(
  entryId: string,
  structuredTable: StructuredTable<unknown, unknown>
): Promise<AddStructuredTableRowsUsingAIResult | null> {
  const result = await runModal(AIFillStructuredTableModal, {entryId, structuredTable});
  if (result == null) {
    return null;
  }
  const filesToAppend =
    result.fileS3Locations.length === 0
      ? []
      : await withLoadingScreen(
          Promise.all(
            result.fileS3Locations.map(
              async (s3Location) => (await NoteLinkedFileDAO.create({location: s3Location}))!
            )
          ),
          'Attaching files...'
        );

  const cidIdMap = {};
  const noteParts = filesToAppend.map((fileToAppend) => {
    const options = {showYear: true, showTime: true};
    const text = `Data entry assistant upload ${DateUtil.readableTime(Date.now(), options)}`;
    const notePart = {
      ...makeDefaultItem(NoteConstants.NOTE_LINKED_OBJECT),
      item_id: fileToAppend.id,
      item: fileToAppend,
      alignment: 'left',
      listType: null,
      placeholder: false,
      text,
    };
    NoteStoreTransforms.prepareNLOForStore(notePart, cidIdMap);
    return notePart;
  });

  return {newRows: result.newRows, noteParts, cidIdMap};
}
